import { useCallback, useMemo } from 'react';

import { useHistory, useLocation } from '@shared/core';
import { useUtmParameters } from '@shared/utils/utmParameters';
import type { SearchParams } from '@shared/utils/accommodations/hotelRoutes';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import { useTravelProductToggleTelemetry } from './TravelProductToggle.telemetry';
import type { MappedSearchParams, TravelProductId } from './TravelProductToggle.types';
import { HotelSearchMapLayout, HotelSearchSource } from '@shared/constants/accommodations';
import { mapCarSearchToHotelSearchParams, mapHotelSearchToCarSearchParams } from './mapSearchParamsBetweenProducts';

type UseTravelProductNavigationArgs = Readonly<{
  eventHandle: string;
  activeProduct: TravelProductId;
}>;

type HotelRouteContext = Pick<SearchParams, 'source' | 'layout'>;

// Default hotel URL fallback (OTA / COMPACT) matching useHotelSearchConfig.
const HOTEL_URL_ROUTE_CONTEXT_FALLBACK: HotelRouteContext = {
  source: HotelSearchSource.OTA,
  layout: HotelSearchMapLayout.COMPACT,
};

// Default car URL fallback (GUEST_MAP / FULL) matching createSearchParamsForGuestMap.
const CAR_URL_ROUTE_CONTEXT_FALLBACK: HotelRouteContext = {
  source: HotelSearchSource.GUEST_MAP,
  layout: HotelSearchMapLayout.FULL,
};

const HOTEL_SEARCH_SOURCES: ReadonlyArray<string> = Object.values(HotelSearchSource);
const HOTEL_SEARCH_LAYOUTS: ReadonlyArray<string> = Object.values(HotelSearchMapLayout);

// Type guards rather than casts — these values come from a user-editable URL.
const isHotelSearchSource = (value: Maybe<string>): value is HotelSearchSource => !!value && HOTEL_SEARCH_SOURCES.includes(value);
const isHotelSearchMapLayout = (value: Maybe<string>): value is HotelSearchMapLayout => !!value && HOTEL_SEARCH_LAYOUTS.includes(value);

// Reads the hotel console's route context (source & layout) off a URLSearchParams object with fallback.
const readHotelRouteContext = (params: URLSearchParams, fallback: HotelRouteContext): HotelRouteContext => {
  const source = params.get('source');
  const layout = params.get('layout');

  return {
    source: isHotelSearchSource(source) ? source : fallback.source,
    layout: isHotelSearchMapLayout(layout) ? layout : fallback.layout,
  };
};

export type UseTravelProductNavigationReturn = Readonly<{
  /** Navigates to the other product in the same tab. A no-op when `product` is already active. */
  navigateToProduct: (product: TravelProductId) => void;
}>;

// Same-tab navigation between hotel search and car search, preserving location, dates, and UTMs.
export const useTravelProductNavigation = ({ eventHandle, activeProduct }: UseTravelProductNavigationArgs): UseTravelProductNavigationReturn => {
  const history = useHistory();
  const location = useLocation();
  const currentUtm = useUtmParameters();
  const hotelRoutePaths = useHotelRoutePaths();
  const telemetry = useTravelProductToggleTelemetry();

  const preservedUtm = useMemo<Pick<SearchParams, 'utm_medium' | 'utm_source' | 'utm_campaign'>>(
    () => ({
      utm_campaign: '',
      utm_medium: 'web',
      utm_source: currentUtm?.utm_source ?? '',
    }),
    [currentUtm?.utm_source],
  );

  const navigateToProduct = useCallback(
    (product: TravelProductId) => {
      if (product === activeProduct) {
        return;
      }

      const currentParams = new URLSearchParams(location.search);
      const isNavigatingToCars = product === 'cars';
      const mapped: MappedSearchParams = isNavigatingToCars ? mapHotelSearchToCarSearchParams(currentParams) : mapCarSearchToHotelSearchParams(currentParams);
      // Carried in both directions so the round trip returns to the original console.
      const routeContext = readHotelRouteContext(currentParams, isNavigatingToCars ? HOTEL_URL_ROUTE_CONTEXT_FALLBACK : CAR_URL_ROUTE_CONTEXT_FALLBACK);
      const searchParams: SearchParams = { ...mapped, ...preservedUtm, ...routeContext };

      const url = isNavigatingToCars
        ? hotelRoutePaths.carSearch.buildRelativeUrl(eventHandle, searchParams)
        : hotelRoutePaths.hotelSearch.buildRelativeUrl(eventHandle, searchParams);

      telemetry.productToggleClicked({ from: activeProduct, to: product, eventHandle });
      history.push(url);
    },
    [activeProduct, eventHandle, history, hotelRoutePaths, location.search, preservedUtm, telemetry],
  );

  return useMemo(() => ({ navigateToProduct }), [navigateToProduct]);
};
